/**
 * SHIRT LIVE PREVIEW - CANVAS HIGH PERFORMANCE EDITION
 * Nombre: "PucelaFont" (CSS) | Números: SVG Oficiales por Metafield
 * Optimizado contra Layout Thrashing y sobrecarga de memoria en móviles.
 */
(() => {
  class ShirtLivePreview {
    constructor(ctx) {
      this.ctx = ctx;
      this.frontId = ctx.dataset.frontMediaId;
      this.rearId = ctx.dataset.rearMediaId;
      this.nameColor = ctx.dataset.nameColor || '#000000';
      this.borderColor = ctx.dataset.bordeColor || null; // <- Añadir esta línea

      this.tpl = ctx.querySelector('[data-live-preview-overlay]');
      if (!this.tpl || !this.rearId) return;

      this.fontMap = JSON.parse(document.querySelector('[data-live-preview-font-map]')?.textContent || '{}');
      
      this.imageCache = {};
      this.preloadSvgs();

      const query = id => Array.from(document.querySelectorAll(`[data-media-id="${id}"],[data-target="${id}"]`)).filter(el => !el.closest('.thumbnails'));
      this.frontEls = query(this.frontId);
      this.rearEls = query(this.rearId);
      if (!this.rearEls.length) return;

      this.isSwapped = false;

      // Almacén estático para elementos del DOM (Mejora drástica de velocidad)
      this.dom = {};
      
      this.init();
    }

    preloadSvgs() {
      Object.entries(this.fontMap).forEach(([num, url]) => {
        const img = new Image();
        img.src = url;
        this.imageCache[num] = img;
        img.onload = () => this.update();
      });

      if (document.fonts) {
        document.fonts.ready.then(() => this.update());
      }
    }

    init() {
      this.overlays = this.rearEls.map((el, i) => {
        if (el.querySelector('[data-live-preview-instance]')) return el.querySelector('[data-live-preview-instance]');
        el.style.position = 'relative';
        
        const clone = this.tpl.cloneNode(true);
        clone.hidden = true;
        clone.dataset.livePreviewInstance = '';
        clone.id = `LivePreview_${i}`;
        
        const canvas = clone.querySelector('canvas');
        if (canvas) {
          canvas.width = 1200;
          canvas.height = 1200;
        }

        el.appendChild(clone);
        return clone;
      }).filter(Boolean);
      this.tpl.remove();

      const gridSelector = '.product-media-collage > *, .product-gallery > *, .grid > *';
      this.frontItem = this.frontEls.map(el => el.closest(gridSelector)).find(Boolean);
      this.rearItem = this.rearEls.map(el => el.closest(gridSelector)).find(Boolean);

      // Cachear referencias fijas del formulario del producto
      this.dom.customRadio = document.querySelector('input[name="personalization_type"][value="custom"]');
      this.dom.playerRadio = document.querySelector('input[name="personalization_type"][value="player"]');
      this.dom.playerSelect = document.querySelector('[data-player-select]');
      this.dom.nameInput = document.querySelector('[data-custom-name]');
      this.dom.numberInput = document.querySelector('[data-custom-number]');

      const triggerUpdate = () => this.update();
      document.addEventListener('change', e => ['personalization_type', 'data-player-select'].some(k => e.target.name === k || e.target.hasAttribute(k)) && triggerUpdate());
      
      // Optimizamos el retraso del debounce a 100ms para suavizar la escritura en móviles
      document.addEventListener('input', this.debounce(e => ['data-custom-name', 'data-custom-number'].some(k => e.target.hasAttribute(k)) && triggerUpdate(), 100));

      this.parseUrlParams();
      this.update();
    }

    /**
     * Lector inteligente de parámetros URL (Tolerante a fallos y acentos)
     */
    parseUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const playerParam = params.get('jugador');
      const nameParam = params.get('nombre');
      const numberParam = params.get('numero');

      const cleanForCompare = (str) => {
        return str.toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .replace(/[\s.-]/g, "");
      };

      if (playerParam && this.dom.playerRadio && this.dom.playerSelect) {
        this.dom.playerRadio.checked = true;
        const cleanedParam = cleanForCompare(playerParam);
        const options = Array.from(this.dom.playerSelect.options);
        
        const match = options.find(opt => 
          cleanForCompare(opt.value).includes(cleanedParam) || 
          cleanForCompare(opt.text).includes(cleanedParam)
        );

        if (match) this.dom.playerSelect.value = match.value;
        this.dom.playerRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } 
      else if ((nameParam || numberParam) && this.dom.customRadio) {
        this.dom.customRadio.checked = true;
        if (this.dom.nameInput && nameParam) this.dom.nameInput.value = nameParam.substring(0, 15); 
        if (this.dom.numberInput && numberParam) this.dom.numberInput.value = numberParam.replace(/\D/g, '').substring(0, 2);

        this.dom.customRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    drawCurvedName(ctx, text, centerX, centerY, radius, fontSize, letterSpacingPixels = 8, fontColor = '#000000', borderColor = null) {
      const cleanText = text.toUpperCase();
      const chars = [...cleanText];
      if (!chars.length) return;

      ctx.save();
      // Configuramos la tipografía antes de medir para tener precisión absoluta
      ctx.font = `${fontSize}px "LALIGAPlayers", sans-serif`;
      ctx.fillStyle = fontColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Configuración del borde si existe
      if (borderColor) {
        ctx.strokeStyle = borderColor;
        // Usamos 3px por la alta resolución del Canvas (1200x1200px). Ajustar si es necesario.
        ctx.lineWidth = 3; 
        ctx.lineJoin = 'round'; // Suaviza las uniones de los vectores de la fuente
      }

      // 1. Calcular el ancho angular de cada letra individual basándonos en su geometría real
      const charAngles = chars.map(char => {
        const metrics = ctx.measureText(char);
        const charWidth = metrics.width;
        // Teorema: Ángulo = Arco / Radio. Sumamos el espaciado extra en píxeles uniformes
        return (charWidth + letterSpacingPixels) / radius;
      });

      // 2. Calcular el ángulo total que ocupará todo el nombre unido
      const totalAngle = charAngles.reduce((sum, angle) => sum + angle, 0);
      
      // 3. El punto de inicio será la mitad del ángulo total a la izquierda del centro absoluto (-Math.PI / 2)
      let currentAngle = -Math.PI / 2 - totalAngle / 2;

      // 4. Dibujar letra a letra posicionando el eje en su propio centro geométrico
      chars.forEach((char, index) => {
        const thisAngle = charAngles[index];
        
        // Avanzamos el cursor angular hasta la mitad de la letra actual para centrar su rotación
        currentAngle += thisAngle / 2;

        ctx.save();
        // Posicionamos en el arco circular
        const x = centerX + radius * Math.cos(currentAngle);
        const y = centerY + radius * Math.sin(currentAngle);
        
        ctx.translate(x, y);
        // Rotamos la letra para que sea perpendicular al radio de la curva
        ctx.rotate(currentAngle + Math.PI / 2);
        
        // Primero dibujamos el borde (atrás)
        if (borderColor) {
          ctx.strokeText(char, 0, 0);
        }
        // Dibujamos el carácter centrado
        ctx.fillText(char, 0, 0);
        ctx.restore();

        // Completamos la otra mitad del ángulo de la letra para dejar paso a la siguiente
        currentAngle += thisAngle / 2;
      });

      ctx.restore();
    }

    drawStraightNumber(ctx, text, centerX, centerY, charHeight, letterSpacing = 15) {
      const chars = [...text.replace(/\s/g, "")];
      if (!chars.length) return;

      let totalWidth = 0;
      const validChars = chars.map(char => {
        const img = this.imageCache[char];
        if (img && img.complete && img.naturalWidth > 0) {
          const w = (img.naturalWidth / img.naturalHeight) * charHeight;
          totalWidth += w;
          return { img, w };
        }
        return null;
      }).filter(Boolean);

      totalWidth += (validChars.length - 1) * letterSpacing;
      let currentX = centerX - totalWidth / 2;

      validChars.forEach(item => {
        ctx.drawImage(item.img, currentX, centerY - charHeight / 2, item.w, charHeight);
        currentX += item.w + letterSpacing;
      });
    }

    update() {
      window.requestAnimationFrame(() => {
        const rad = document.querySelector('input[name="personalization_type"]:checked');
        const active = rad && (rad.value === 'player' || rad.value === 'custom');
        const isDesktop = window.matchMedia('(min-width: 769px)').matches;

        this.overlays.forEach(ov => ov.hidden = !active);

        if (active) {
          let name = '', num = '';
          
          if (rad.value === 'player' && this.dom.playerSelect) {
            if (this.dom.playerSelect.selectedIndex > 0) { 
              name = this.dom.playerSelect.value || name; 
              num = this.dom.playerSelect.options[this.dom.playerSelect.selectedIndex].dataset.number || num; 
            }
          } else {
            name = this.dom.nameInput?.value.trim() || name;
            num = this.dom.numberInput?.value.trim() || num;
          }

          this.overlays.forEach(ov => {
            const canvas = ov.querySelector('canvas');
            const context = canvas.getContext('2d');
            
            context.clearRect(0, 0, canvas.width, canvas.height);
            
            // =================================================================
            // RECUADRO DE MEDIDAS OPTIMIZADO (1200x1200px)
            // =================================================================
            const nameCenterX = 600;       
            const nameCenterY = 800;  // Bajamos un pelín el centro de rotación para suavizar la curva
            const nameRadius = 580;   // Aumentamos el radio para que la curva sea más abierta y natural como en la real
            const nameSize = 90;      // Tamaño estilizado de la tipografía
            const nameSpacing = 1.5;   // ¡NUEVO! Separación limpia en píxeles entre letras (ajusta a tu gusto)

            this.drawCurvedName(context, name, nameCenterX, nameCenterY, nameRadius, nameSize, nameSpacing, this.nameColor, this.borderColor);

            // 2. Dorsales SVG
            const numCenterX = 600;
            const numCenterY = 535;
            const numHeight = 370;         // Altura de la imagen del dorsal
            const numSpacing = 25;         // Separación entre dígitos         
            
            this.drawStraightNumber(context, num, numCenterX, numCenterY, numHeight, numSpacing);
          });

          if (isDesktop && this.frontItem && this.rearItem && !this.isSwapped) {
            this.frontItem.parentNode.insertBefore(this.rearItem, this.frontItem);
            this.isSwapped = true;
          } else if (!isDesktop && !this.isSwapped) {
            document.querySelector(`.thumbnails [data-media-id="${this.rearId}"] button, .thumbnails [data-media-id="${this.rearId}"] a, .thumbnails a[href*="${this.rearId}"]`)?.click();
            this.isSwapped = true;
          }
        } else if (this.isSwapped) {
          if (isDesktop && this.frontItem && this.rearItem) {
            this.rearItem.parentNode.insertBefore(this.frontItem, this.rearItem);
          } else if (!isDesktop) {
            document.querySelector(`.thumbnails [data-media-id="${this.frontId}"] button, .thumbnails [data-media-id="${this.frontId}"] a, .thumbnails a[href*="${this.frontId}"]`)?.click();
          }
          this.isSwapped = false;
        }
      });
    }

    debounce(fn, delay) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
    }
  }

  const init = () => document.querySelectorAll('[data-shirt-preview]:not([data-live-init])').forEach(el => { el.dataset.liveInit = 'true'; new ShirtLivePreview(el); });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('shopify:section:load', init);
})();